/** Transition table for one state: which events it accepts and what state each leads to. */
export type Transitions<TState extends string, TEvent extends string> = Partial<Record<TEvent, TState>>;

export interface FSMConfig<TState extends string, TEvent extends string> {
  initial: TState;
  transitions: Record<TState, Transitions<TState, TEvent>>;
  onEnter?: Partial<Record<TState, (event: TEvent | null, previous: TState | null) => void>>;
  onExit?: Partial<Record<TState, (event: TEvent, next: TState) => void>>;
}

/**
 * Minimal explicit-transition-table state machine. Illegal events for the current state are
 * rejected (dispatch returns false) rather than throwing, since AI stimuli arrive from gameplay
 * code that shouldn't need to know which states currently care about which events.
 */
export class FiniteStateMachine<TState extends string, TEvent extends string> {
  private readonly config: FSMConfig<TState, TEvent>;
  private currentState: TState;

  constructor(config: FSMConfig<TState, TEvent>) {
    this.config = config;
    this.currentState = config.initial;
    this.config.onEnter?.[this.currentState]?.(null, null);
  }

  get state(): TState {
    return this.currentState;
  }

  canDispatch(event: TEvent): boolean {
    return this.config.transitions[this.currentState]?.[event] !== undefined;
  }

  dispatch(event: TEvent): boolean {
    const next = this.config.transitions[this.currentState]?.[event];
    if (next === undefined) return false;

    const previous = this.currentState;
    this.config.onExit?.[previous]?.(event, next);
    this.currentState = next;
    this.config.onEnter?.[next]?.(event, previous);
    return true;
  }
}
